import { useState } from "react";
import { Outlet, useNavigate } from "react-router-dom";

import { asSeasonId } from "../domain/ids.js";
import { useItems } from "../hooks/useItems.js";
import { useSeasons } from "../hooks/useSeasons.js";
import { useIsNarrow } from "../hooks/useViewport.js";
import { CloseSeasonDialog } from "./CloseSeasonDialog.js";
import { FiltersBar } from "./FiltersBar.js";
import { ItemCards } from "./ItemCards.js";
import { ItemTable } from "./ItemTable.js";
import { useListFilters } from "./useListFilters.js";
import "./list.css";

/** Max the API allows per page (`listItemsQuery`, apps/server/src/schemas/items.ts). */
const PAGE_SIZE = 200;

/** Replaces `MainWindow.xaml`: the filtered, virtualised list of everything currently (or historically) stored. */
export function ItemListPage() {
  const navigate = useNavigate();
  const filters = useListFilters();
  const isNarrow = useIsNarrow();
  const [closeSeasonOpen, setCloseSeasonOpen] = useState(false);

  const seasonsQuery = useSeasons();
  const seasons = seasonsQuery.data ?? [];
  // sortSeasons (applied server-side) puts the unnamed open season first when one exists.
  const openSeason = seasons.find((s) => s.isOpen) ?? null;
  const effectiveSeasonId = filters.seasonId ?? openSeason?.id ?? seasons[0]?.id ?? null;

  const itemsQuery = useItems({
    ...(effectiveSeasonId ? { seasonId: asSeasonId(effectiveSeasonId) } : {}),
    ...(filters.pickedUp !== "all" ? { pickedUp: filters.pickedUp === "yes" } : {}),
    ...(filters.search.trim() ? { search: filters.search.trim() } : {}),
    limit: PAGE_SIZE,
    offset: filters.offset,
  });

  function handleActivate(id: string) {
    navigate(`/items/${id}`);
  }

  const items = itemsQuery.data?.items ?? [];
  const total = itemsQuery.data?.total ?? 0;

  return (
    <div className="item-list-page">
      <FiltersBar
        seasons={seasons}
        seasonId={effectiveSeasonId}
        onSeasonChange={filters.setSeason}
        pickedUp={filters.pickedUp}
        onPickedUpChange={filters.setPickedUp}
        search={filters.search}
        onSearchChange={filters.setSearch}
        onNewItem={() => navigate("/items/new")}
        onCloseSeason={() => setCloseSeasonOpen(true)}
        canCloseSeason={openSeason !== null}
      />

      <div className="item-list-body">
        {itemsQuery.isPending && (
          <p className="item-list-status" role="status">
            Nalaganje …
          </p>
        )}
        {itemsQuery.isError && (
          <p className="item-list-status" role="alert">
            Napaka pri nalaganju seznama.
          </p>
        )}
        {itemsQuery.isSuccess && items.length === 0 && <p className="item-list-status">Ni vnosov, ki bi ustrezali filtrom.</p>}
        {itemsQuery.isSuccess &&
          items.length > 0 &&
          (isNarrow ? <ItemCards items={items} onActivate={handleActivate} /> : <ItemTable items={items} onActivate={handleActivate} />)}
      </div>

      {total > PAGE_SIZE && (
        <div className="item-list-pagination">
          <button type="button" disabled={filters.offset === 0} onClick={() => filters.setOffset(Math.max(0, filters.offset - PAGE_SIZE))}>
            Prejšnja
          </button>
          <span>
            {filters.offset + 1}–{Math.min(filters.offset + PAGE_SIZE, total)} od {total}
          </span>
          <button
            type="button"
            disabled={filters.offset + PAGE_SIZE >= total}
            onClick={() => filters.setOffset(filters.offset + PAGE_SIZE)}
          >
            Naslednja
          </button>
        </div>
      )}

      {closeSeasonOpen && openSeason && (
        <CloseSeasonDialog
          openSeason={openSeason}
          onClose={() => setCloseSeasonOpen(false)}
          onClosed={(opened) => {
            filters.setSeason(opened.id);
            setCloseSeasonOpen(false);
          }}
        />
      )}

      {/* The intake/edit form modal (`/items/new`, `/items/:id`) renders here,
          floating over the list rather than replacing it. */}
      <Outlet />
    </div>
  );
}
